import axios from 'axios'

/**
 * Captures client-side JavaScript errors and sends them to the server.
 * Intercepts: console.error, window.onerror, unhandledrejection
 * Batches entries and flushes every 10 seconds or when buffer hits 10 entries.
 */

const buffer = []
let flushTimer = null
const MAX_BUFFER = 10
const FLUSH_INTERVAL = 10000 // 10 seconds

/**
 * Messages to ignore — these are not real errors:
 * - Network failures (user's connection dropped)
 * - Chunk loading failures (slow connection timeouts)
 * - ResizeObserver noise (benign browser warning)
 * - Navigation cancellations (Vue Router expected behavior)
 * - Login 401/403 (expected for wrong credentials)
 * - Redundant navigation (Vue Router duplicate nav)
 * - Script error (cross-origin, no useful info)
 */
const IGNORE_PATTERNS = [
  'Network error',
  'NetworkError',
  'Network Error',
  'Failed to fetch',
  'Load failed',
  'fetch failed',
  'Loading chunk',
  'Loading CSS chunk',
  'ResizeObserver',
  'Navigation cancelled',
  'Avoided redundant navigation',
  'Request aborted',
  'Request failed with status code 401',
  'Request failed with status code 403',
  'Request failed with status code 502',
  'Script error',
  'Permission denied (by RLS)',
  'current transaction is aborted',
  'jwt.claims.app_user_id',
  '[authGuard]',
  'Login error:',
  'Reset password error:',
  'Redirected when going from',
  'get_app_user_info',
  'WKWebView API client did not respond',
  'The user aborted a request'
]

function shouldIgnore (message) {
  if (!message) return false
  return IGNORE_PATTERNS.some(pattern => message.includes(pattern))
}

function flush () {
  if (buffer.length === 0) return
  const entries = buffer.splice(0, MAX_BUFFER)
  axios.post('/api/auth/client-logs', { entries }, { withCredentials: true }).catch(() => {
    // Silently ignore — don't create error loops
  })
}

function pushEntry (entry) {
  if (shouldIgnore(entry.message)) return
  buffer.push(entry)
  if (buffer.length >= MAX_BUFFER) {
    flush()
  }
}

function startFlushTimer () {
  if (flushTimer) return
  flushTimer = setInterval(flush, FLUSH_INTERVAL)
}

export function initClientErrorCapture () {
  // Override console.error
  const originalError = console.error
  console.error = function (...args) {
    originalError.apply(console, args)
    try {
      const message = args.map(a => {
        if (a instanceof Error) return a.message
        if (typeof a === 'object') {
          try { return JSON.stringify(a) } catch { return String(a) }
        }
        return String(a)
      }).join(' ')

      pushEntry({
        level: 'error',
        message: message.slice(0, 2000),
        stack: (args.find(a => a instanceof Error))?.stack || null,
        url: window.location.href,
        timestamp: new Date().toISOString()
      })
    } catch {
      // Never break the app
    }
  }

  // Global error handler (uncaught exceptions)
  window.addEventListener('error', (event) => {
    try {
      pushEntry({
        level: 'uncaught',
        message: String(event.message || '').slice(0, 2000),
        stack: event.error?.stack || null,
        url: event.filename || window.location.href,
        line: event.lineno,
        col: event.colno,
        timestamp: new Date().toISOString()
      })
    } catch {
      // Never break the app
    }
  })

  // Unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    try {
      const reason = event.reason
      const message = reason instanceof Error ? reason.message : String(reason || 'Unhandled promise rejection')
      pushEntry({
        level: 'unhandled-rejection',
        message: message.slice(0, 2000),
        stack: reason instanceof Error ? reason.stack : null,
        url: window.location.href,
        timestamp: new Date().toISOString()
      })
    } catch {
      // Never break the app
    }
  })

  // Flush remaining entries before page unload
  window.addEventListener('beforeunload', () => {
    if (buffer.length > 0) {
      const entries = buffer.splice(0)
      // Use sendBeacon for reliable delivery during unload
      try {
        navigator.sendBeacon('/api/auth/client-logs', new Blob([JSON.stringify({ entries })], { type: 'application/json' }))
      } catch {
        // Fall back silently
      }
    }
  })

  startFlushTimer()
}
